import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { type PermissionFeatures } from "../../SettingsPermissions/types/permissions.types.ts"
import { useOpensCommunicationAutomations } from "../../Communication/hub/moved-redirects.tsx"
import { useCompanyDefaultsAccess } from "../components/common/company-defaults-access.ts"

export type AutomationSection = {
  key: "communication" | "status" | "requiredInformation"
  route: string
  labelKey: string
}

// One source for both the top-level Automation tab and its sub-tab strip, so
// the tab never opens on a section the strip hides.
export function useAutomationSections(
  permissions?: PermissionFeatures
): AutomationSection[] {
  const storedPermissions = useAppSelector(
    (state) => state.auth?.userInfo?.permissions?.codes
  )
  const codes = permissions ?? storedPermissions
  const leadAutomationOn = useAppSelector(
    (state) => !!state.auth?.userInfo?.feature_lead_automation
  )
  // Status automation and Required Information are Company Defaults blocks:
  // the server's access answer decides them like every other block.
  const { canViewBlock } = useCompanyDefaultsAccess()
  // Communication automations are edited in Communication → Automations; the
  // Settings copy stays only for someone who cannot open that tab.
  const movedToCommunication = useOpensCommunicationAutomations()

  const sections: [AutomationSection, boolean][] = [
    [
      {
        key: "communication",
        route: RoutesPaths.settings.automation,
        labelKey: "settingsAutomation.sections.communication"
      },
      leadAutomationOn &&
        !movedToCommunication &&
        !!codes?.settings?.automations?.settings_automation_view
    ],
    [
      {
        key: "status",
        route: RoutesPaths.settings.statusAutomation,
        labelKey: "settingsAutomation.sections.status"
      },
      canViewBlock("status_automation")
    ],
    [
      {
        key: "requiredInformation",
        route: RoutesPaths.settings.formFieldRequirements,
        labelKey: "settingsAutomation.sections.requiredInformation"
      },
      canViewBlock("required_information")
    ]
  ]

  return sections.filter(([, visible]) => visible).map(([section]) => section)
}
